/**
 * OAuth Callback Page
 * Handles OAuth redirect from external providers
 * This page runs in the popup window and communicates with the parent window
 */

import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

export const OAuthCallback: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Get OAuth response parameters from URL. TikTok's Marketing API returns the
    // authorization code as `auth_code` instead of the standard `code`.
    const code = searchParams.get("code") ?? searchParams.get("auth_code");
    const state = searchParams.get("state");
    const error = searchParams.get("error");
    const errorDescription = searchParams.get("error_description");

    // Send message to parent window
    if (window.opener) {
      const message = {
        code,
        state,
        error: error || errorDescription || undefined,
      };

      // Send message to parent window
      window.opener.postMessage(message, window.location.origin);

      // Close the popup after a short delay to ensure message is delivered
      setTimeout(() => {
        window.close();
      }, 100);
    } else {
      // No parent window - this might be opened directly
      document.body.innerHTML = `
        <div style="display: flex; align-items: center; justify-content: center; height: 100vh; font-family: system-ui; background: #f3f4f6;">
          <div style="text-align: center; padding: 2rem;">
            <h1 style="color: #dc2626; margin-bottom: 0.5rem;">${t("misc.oauthErrorTitle")}</h1>
            <p style="color: #6b7280;">${t("misc.oauthOpenedDirectly")}</p>
            <p style="color: #6b7280; margin-top: 1rem;">${t("misc.oauthCloseWindow")}</p>
          </div>
        </div>
      `;
    }
  }, [searchParams, t]);

  // Show loading state while popup is open
  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height: "100vh",
      fontFamily: "system-ui, -apple-system, sans-serif",
      background: "#f3f4f6",
    }}>
      <div style={{ textAlign: "center", padding: "2rem" }}>
        <div style={{
          width: "48px",
          height: "48px",
          border: "4px solid #e5e7eb",
          borderTopColor: "#3b82f6",
          borderRadius: "50%",
          animation: "spin 1s linear infinite",
          margin: "0 auto 1rem"
        }} />
        <p style={{ color: "#6b7280", fontSize: "1rem" }}>
          {t('misc.oauthConnecting')}
        </p>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </div>
  );
};

export default OAuthCallback;
